/**
 * Centralised TanStack Query keys. Always build keys from here — string
 * literals scattered in features make cache invalidation refactors unsafe.
 *
 * Prefix arrays (e.g. `listings.all`) are for `invalidateQueries` /
 * `setQueriesData` matching; functions build the exact key of one query.
 */
export const queryKeys = {
  listings: {
    /** Prefix matching every `/app/biens` list variant (all filter combos). */
    all: ['listings', 'all'] as const,
    list: (params?: Record<string, unknown>) => ['listings', 'all', params ?? {}] as const,
    /** Map-view fetch-all variant (own page set, same invalidation prefix). */
    mapAll: (params?: Record<string, unknown>) =>
      ['listings', 'all', 'map', params ?? {}] as const,
  },
  listingFilters: ['listing-filters'] as const,
  /** Row counts of the CSV-export modal (one entry per option's param set). */
  exportCount: (params: Record<string, unknown>) => ['export-count', params] as const,
  fiche: (identifiantDpe: string) => ['fiche', identifiantDpe] as const,
  dvf: (identifiant: string) => ['dvf', identifiant] as const,
  searches: ['searches'] as const,
  search: (id: string) => ['search', id] as const,
  searchFilters: ['search-filters'] as const,
  searchStats: (period: string) => ['search-stats', period] as const,
  subscription: ['subscription'] as const,
  onboarding: ['onboarding'] as const,
  invoices: ['invoices'] as const,
  lists: ['lists'] as const,
  list: (id: string) => ['list', id] as const,
  notifications: ['notifications'] as const,
  notificationListings: (id: number) => ['notifications', 'listings', id] as const,
  unseenCount: ['unseen-count'] as const,
  /**
   * Invité /app/decouverte. Le token signé n'entre JAMAIS dans une clé (visible en
   * devtools React Query) — il reste capturé dans les queryFn.
   */
  prospect: {
    results: ['prospect', 'results'] as const,
    fiche: (identifiantDpe: string) => ['prospect', 'fiche', identifiantDpe] as const,
  },
  adminProspects: {
    /** Préfixe d'invalidation après revoke/suppression RGPD. */
    all: ['admin', 'prospects'] as const,
    list: (params: Record<string, unknown>) => ['admin', 'prospects', params] as const,
  },
  adminProspect: (id: string) => ['admin', 'prospect', id] as const,
  adminCampaigns: (groupBy: string) => ['admin', 'campaigns', groupBy] as const,
  campaignDailyStats: (params: Record<string, unknown>) =>
    ['admin', 'campaigns', 'daily-stats', params] as const,
  adminUsers: (page: number, search = '', sortBy = '', sortOrder = '') =>
    ['admin', 'users', page, search, sortBy, sortOrder] as const,
  adminUser: (id: string) => ['admin', 'user', id] as const,
  adminUserList: (userId: string, listId: string) =>
    ['admin', 'user', userId, 'list', listId] as const,
  tierSettings: (tier: string) => ['admin', 'tier-settings', tier] as const,
  adminOnboardingDashboard: ['admin', 'onboardings', 'dashboard'] as const,
  adminOnboardings: (page: number) => ['admin', 'onboardings', 'texts', page] as const,
}
